import { resolveSalesMultiCustomerName, resolveSalesMultiCustomerStatusCategory } from '@/composables/salesMultiCustomer/useSalesMultiCustomerStatusTabs';

const DEFAULT_CURRENCY = 'MXN';
const MAX_TOTAL_CURRENCIES = 3;

const normalizeCurrencyCode = (value) => {
  const code = String(value || '').trim().toUpperCase();
  return code || DEFAULT_CURRENCY;
};

const normalizeTotalByCurrency = (rawTotals = [], fallbackTotal = 0) => {
  const totals = Array.isArray(rawTotals) ? rawTotals : [];
  const grouped = {};

  totals.forEach((item) => {
    const currency = normalizeCurrencyCode(item?.currency);
    const total = Number(item?.total ?? 0) || 0;

    grouped[currency] = (grouped[currency] || 0) + total;
  });

  if (Object.keys(grouped).length === 0 && Number(fallbackTotal || 0) !== 0) {
    grouped[DEFAULT_CURRENCY] = Number(fallbackTotal || 0);
  }

  return Object.entries(grouped)
    .map(([currency, total]) => ({ currency, total: Number(total || 0) }))
    .filter((item) => Number.isFinite(item.total));
};

const orderTotalByCurrency = (totals = []) => {
  const safeTotals = Array.isArray(totals) ? totals : [];

  return [...safeTotals].sort((left, right) => {
    const leftCode = normalizeCurrencyCode(left?.currency);
    const rightCode = normalizeCurrencyCode(right?.currency);

    if (leftCode === DEFAULT_CURRENCY && rightCode !== DEFAULT_CURRENCY) {
      return -1;
    }

    if (rightCode === DEFAULT_CURRENCY && leftCode !== DEFAULT_CURRENCY) {
      return 1;
    }

    return leftCode.localeCompare(rightCode);
  });
};

const buildLatestSaleLabel = (latestSale = {}) => {
  const code = String(latestSale?.code || '').trim();
  const name = String(latestSale?.name || '').trim();

  if (code && name) {
    return `${code} - ${name}`;
  }

  return code || name || '-';
};

const formatDateLabel = (value) => {
  if (!value) {
    return '-';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }

  return new Intl.DateTimeFormat('es-MX', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(parsed);
};

const isSameCalendarDay = (leftDate, rightDate) => {
  if (!(leftDate instanceof Date) || Number.isNaN(leftDate.getTime())) {
    return false;
  }

  if (!(rightDate instanceof Date) || Number.isNaN(rightDate.getTime())) {
    return false;
  }

  return leftDate.getFullYear() === rightDate.getFullYear()
    && leftDate.getMonth() === rightDate.getMonth()
    && leftDate.getDate() === rightDate.getDate();
};

const buildLatestSaleMeta = (latestSale = {}) => {
  const createdAt = new Date(latestSale?.created_at || 0);
  if (isSameCalendarDay(createdAt, new Date())) {
    return 'Fecha creación: Hoy';
  }

  return `Fecha creación: ${formatDateLabel(latestSale?.created_at)}`;
};

const buildCurrentMonthLabel = () => {
  const now = new Date();
  const formatted = new Intl.DateTimeFormat('es-MX', {
    month: 'long',
    year: 'numeric',
  }).format(now);

  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

const formatCount = (value) => {
  const count = Number(value || 0);
  const safeCount = Number.isFinite(count) && count > 0 ? count : 0;

  return new Intl.NumberFormat('es-MX', {
    maximumFractionDigits: 0,
  }).format(safeCount);
};

const buildLatestSaleRoute = (latestSale = {}) => {
  const id = Number(latestSale?.id || 0);
  if (!Number.isFinite(id) || id <= 0) {
    return null;
  }

  return {
    name: 'saleDetail',
    params: {
      saleId: id,
    },
  };
};

const buildTotalDisplay = (metricsValues, formatCurrency) => {
  const totalsByCurrency = orderTotalByCurrency(metricsValues?.total_by_currency || [])
    .slice(0, MAX_TOTAL_CURRENCIES);

  if (totalsByCurrency.length === 0) {
    return {
      value: formatCurrency(metricsValues?.total || 0),
      extraValues: [],
    };
  }

  const formatLine = (item, isFirst = false) => {
    const code = normalizeCurrencyCode(item?.currency);
    const amount = formatCurrency(item?.total || 0);

    if (code === DEFAULT_CURRENCY && isFirst) {
      return amount;
    }

    return `${code} ${amount}`;
  };

  return {
    value: formatLine(totalsByCurrency[0], true),
    extraValues: totalsByCurrency.slice(1).map((item) => formatLine(item)),
  };
};

export const createInitialSalesMultiCustomerMetricsValues = () => ({
  total: 0,
  total_open_businesses: 0,
  total_billed: 0,
  new_businesses_current_month: 0,
  total_by_currency: [],
  latest_sale: {
    id: 0,
    code: '',
    name: '',
    created_at: null,
  },
});

export const mapSalesMultiCustomerMetricsPayload = (payload) => {
  const latestSale = payload?.latest_sale && typeof payload.latest_sale === 'object'
    ? payload.latest_sale
    : {};

  const total = Number(payload?.total ?? payload?.total_forecast ?? payload?.forecast ?? 0) || 0;

  return {
    total,
    total_open_businesses: Number(
      payload?.total_open_businesses ?? payload?.total_balance ?? payload?.balance ?? payload?.saldo ?? 0,
    ) || 0,
    total_billed: Number(payload?.total_billed ?? payload?.facturado ?? 0) || 0,
    new_businesses_current_month: Number(payload?.new_businesses_current_month ?? 0) || 0,
    total_by_currency: normalizeTotalByCurrency(payload?.total_by_currency, total),
    latest_sale: {
      id: Number(latestSale?.id ?? 0) || 0,
      code: String(latestSale?.code ?? payload?.last_sale ?? payload?.last_business ?? '').trim(),
      name: String(latestSale?.name ?? '').trim(),
      created_at: latestSale?.created_at || null,
    },
  };
};

export const buildSalesMultiCustomerMetricsFromRows = (rows) => {
  const safeRows = Array.isArray(rows) ? rows : [];

  const totals = safeRows.reduce((acc, row) => {
    const subtotal = Number(row?.forecast_total ?? row?.subtotal ?? row?.total ?? 0) || 0;
    const balance = Number(row?.balance ?? row?.saldo ?? 0) || 0;
    const billed = Number(row?.total_billed ?? row?.billed ?? (subtotal - balance) ?? 0) || 0;
    const currency = normalizeCurrencyCode(row?.currency);
    const isOpen = resolveSalesMultiCustomerStatusCategory(row) === 'open';

    acc.total += subtotal;
    acc.total_open_businesses += isOpen ? subtotal : 0;
    acc.total_billed += billed;
    acc.total_by_currency[currency] = (acc.total_by_currency[currency] || 0) + subtotal;

    const createdAt = new Date(row?.created_at || row?.createdAt || row?.date || row?.sale_date || 0);
    if (!Number.isNaN(createdAt.getTime())) {
      const now = new Date();
      if (createdAt.getFullYear() === now.getFullYear() && createdAt.getMonth() === now.getMonth()) {
        acc.new_businesses_current_month += 1;
      }
    }

    return acc;
  }, {
    total: 0,
    total_open_businesses: 0,
    total_billed: 0,
    new_businesses_current_month: 0,
    total_by_currency: {},
  });

  const latest = [...safeRows]
    .sort((left, right) => {
      const leftDate = new Date(left?.created_at || left?.createdAt || left?.sale_date || left?.date || 0).getTime();
      const rightDate = new Date(right?.created_at || right?.createdAt || right?.sale_date || right?.date || 0).getTime();

      return rightDate - leftDate;
    })
    .find((row) => resolveSalesMultiCustomerName(row) !== '-');

  const totalsByCurrency = Object.entries(totals.total_by_currency).map(([currency, total]) => ({
    currency,
    total: Number(total || 0),
  }));

  return {
    total: totals.total,
    total_open_businesses: totals.total_open_businesses,
    total_billed: totals.total_billed,
    new_businesses_current_month: totals.new_businesses_current_month,
    total_by_currency: normalizeTotalByCurrency(totalsByCurrency, totals.total),
    latest_sale: {
      id: Number(latest?.id ?? 0) || 0,
      code: String(latest?.code || '').trim(),
      name: String(latest?.sale_name || latest?.name || '').trim(),
      created_at: latest?.created_at || latest?.createdAt || null,
    },
  };
};

export const buildSalesMultiCustomerKpiCards = (metricsValues, formatCurrency) => {
  const totalDisplay = buildTotalDisplay(metricsValues, formatCurrency);
  const currentMonthLabel = buildCurrentMonthLabel();

  return [
    {
      title: 'Total',
      value: totalDisplay.value,
      extraValues: totalDisplay.extraValues,
      icon: 'fa-line-chart',
      iconBg: 'rgba(0, 112, 205, 0.12)',
      iconColor: '#0070cd',
    },
    {
      title: 'Total negocios abiertos',
      value: formatCurrency(metricsValues?.total_open_businesses),
      extraValues: [],
      icon: 'fa-balance-scale',
      iconBg: 'rgba(77, 191, 180, 0.12)',
      iconColor: '#4dbfb4',
    },
    {
      title: `Nuevos negocios (${currentMonthLabel})`,
      value: formatCount(metricsValues?.new_businesses_current_month),
      extraValues: [],
      icon: 'fa-bank',
      iconBg: 'rgba(0, 178, 226, 0.12)',
      iconColor: '#00b2e2',
    },
    {
      title: 'ULTIMO NEGOCIO CREADO',
      value: buildLatestSaleLabel(metricsValues?.latest_sale),
      valueRoute: buildLatestSaleRoute(metricsValues?.latest_sale),
      valueLinkClass: 'kpi-value-link-customer',
      valueScale: 0.5625,
      truncateValue: true,
      valueMeta: buildLatestSaleMeta(metricsValues?.latest_sale),
      extraValues: [],
      icon: 'fa-briefcase',
      iconBg: 'rgba(1, 191, 210, 0.12)',
      iconColor: '#01bfd2',
    },
  ];
};
